import { useEffect, useRef } from 'react';
import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import { highlightAssistantTargets } from 'utils/assistantHighlight';
import { MainMoFilter, moSearchFilter } from 'constants/mo';
import { Select } from 'ts/interfaces/Generic';
import { SearchRequest } from 'ts/shared/interfaces/MOResponse';

export interface OrdersSearchAction {
  checkIn?: string;
  checkOut?: string;
  searchByCreateDate?: boolean;
  status?: string | null;
  supplier?: string | null;
  product?: string | null;
  department?: string | null;
  branch?: string | null;
  user?: string | null;
  segmentText?: string | null;
  searchByOrderId?: boolean;
  supplierPnr?: string | null;
  excludeDates?: boolean;
  backOfficeId?: string | null;
  passenger?: string | null;
  item?: string | null;
  filter?: string | null;
}

export interface AppliedOrdersSearch {
  model: SearchRequest;
  options?: SearchRequest;
  segmentText?: string | null;
  mainFilter?: string;
  orderFilter?: string;
}

const PRODUCT_ALIASES: Record<string, string> = {
  hotel: 'HTL',
  hotels: 'HTL',
  htl: 'HTL',
  flight: 'FLT',
  flights: 'FLT',
  flt: 'FLT',
  transfer: 'TRS',
  transfers: 'TRS',
  trs: 'TRS',
  package: 'SMPK',
  packages: 'SMPK',
  smpk: 'SMPK',
  il: 'SMPK',
  activity: 'ACT',
  activities: 'ACT',
  act: 'ACT',
  insurance: 'INS',
  ins: 'INS',
  cruise: 'CRU',
  cru: 'CRU',
  train: 'TRN',
  trn: 'TRN',
  meal: 'MEALM',
  mealm: 'MEALM',
  dynamic: 'PCKM',
  pckm: 'PCKM',
  sport: 'SACT',
  sact: 'SACT',
};

const STATUS_ALIASES: Record<string, string> = {
  all: 'ALL',
  any: 'ALL',
  voucher: 'VOUCHER',
  vouchers: 'VOUCHER',
  ok: 'OK',
  confirmed: 'OK',
  rq: 'RQ',
  request: 'RQ',
  'on request': 'RQ',
  hk: 'HK',
  hx: 'HX',
  ip: 'IP',
  ap: 'AP',
  err: 'ERR',
  error: 'ERR',
  yk: 'YK',
};

const FILTER_ALIASES: Record<string, string> = {
  none: MainMoFilter.NoFilter,
  'no filter': MainMoFilter.NoFilter,
  clear: MainMoFilter.NoFilter,
  duplication: MainMoFilter.OnlyDuplication,
  duplicates: MainMoFilter.OnlyDuplication,
  duplicate: MainMoFilter.OnlyDuplication,
  'only duplication': MainMoFilter.OnlyDuplication,
  'cxl 24': MainMoFilter.CXLLess24Hours,
  'cxl 48': MainMoFilter.CXLLess48Hours,
  'cxl 72': MainMoFilter.CXLLess72Hours,
  'cxl 96': MainMoFilter.CXLLess96Hours,
  'cxl less 24 hours': MainMoFilter.CXLLess24Hours,
  'cxl less 48 hours': MainMoFilter.CXLLess48Hours,
  'cxl less 72 hours': MainMoFilter.CXLLess72Hours,
  'cxl less 96 hours': MainMoFilter.CXLLess96Hours,
  'less 24': MainMoFilter.CXLLess24Hours,
  'less 48': MainMoFilter.CXLLess48Hours,
  'less 72': MainMoFilter.CXLLess72Hours,
  'less 96': MainMoFilter.CXLLess96Hours,
  temporary: MainMoFilter.OnlyWithTemporaryNames,
  'temporary names': MainMoFilter.OnlyWithTemporaryNames,
  'temporary name': MainMoFilter.OnlyWithTemporaryNames,
  'only with temporary names': MainMoFilter.OnlyWithTemporaryNames,
  open: MainMoFilter.Open,
  'open op': MainMoFilter.Open,
  close: MainMoFilter.Close,
  closed: MainMoFilter.Close,
  'close cp': MainMoFilter.Close,
  refund: MainMoFilter.RefundReport,
  'refund report': MainMoFilter.RefundReport,
  'change name': MainMoFilter.ChangeNameReport,
  'name change': MainMoFilter.ChangeNameReport,
  'keep del': MainMoFilter.KeepDelRows,
  'keep delrows': MainMoFilter.KeepDelRows,
  'keep del rows': MainMoFilter.KeepDelRows,
  deleted: MainMoFilter.KeepDelRows,
  'last month': MainMoFilter.LastMonthSegments,
  'previous month': MainMoFilter.LastMonthSegments,
  'last month segments': MainMoFilter.LastMonthSegments,
  'current month': MainMoFilter.CurrentMonthSegments,
  'this month': MainMoFilter.CurrentMonthSegments,
  'current month segments': MainMoFilter.CurrentMonthSegments,
  coupon: MainMoFilter.CouponCode,
  'coupon code': MainMoFilter.CouponCode,
};

const norm = (value: string) => value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

const isAll = (value: string) => ['all', 'any', '*', '-1', '[all]'].includes(norm(value));

const fmt = (date: Date) => format(date, 'yyyy-MM-dd');

const asBool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const key = norm(value);
    if (['true', '1', 'yes'].includes(key)) return true;
    if (['false', '0', 'no'].includes(key)) return false;
  }
  return undefined;
};

const read = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }
  return undefined;
};

const asText = (value: unknown): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value.map((item) => (item == null ? '' : String(item).trim())).filter(Boolean);
    return parts.length ? parts.join(' ') : null;
  }
  return undefined;
};

const toOrderDate = (value: string): string | null => {
  const trimmed = value.trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, '0');
    const month = dmy[2].padStart(2, '0');
    const year = dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3];
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return fmt(parsed);
};

const relativeRange = (value: string): { from: string; to: string } | null => {
  const key = norm(value);
  const now = new Date();
  if (key === 'today') return { from: fmt(now), to: fmt(now) };
  if (key === 'yesterday') {
    const day = subDays(now, 1);
    return { from: fmt(day), to: fmt(day) };
  }
  if (key === 'last week' || key === 'past week') {
    return { from: fmt(subDays(now, 7)), to: fmt(now) };
  }
  if (key === 'last month' || key === 'previous month') {
    const month = subMonths(now, 1);
    return { from: fmt(startOfMonth(month)), to: fmt(endOfMonth(month)) };
  }
  if (key === 'this month' || key === 'current month') {
    return { from: fmt(startOfMonth(now)), to: fmt(endOfMonth(now)) };
  }
  return null;
};

const flattenPayload = (raw: Record<string, unknown>) => {
  const query = raw.query;
  if (query && typeof query === 'object' && !Array.isArray(query)) {
    return { ...raw, ...(query as Record<string, unknown>) };
  }
  return raw;
};

export const parseSearchOrdersPayload = (
  raw: Record<string, unknown> | null | undefined,
): OrdersSearchAction => {
  if (!raw || typeof raw !== 'object') return {};
  const source = flattenPayload(raw);
  const action: OrdersSearchAction = {};

  const fromRaw = asText(read(source, ['orderCheckIn', 'checkIn', 'from', 'startDate', 'dateFrom']));
  const toRaw = asText(read(source, ['orderCheckOut', 'checkOut', 'to', 'endDate', 'dateTo']));
  const fromRange = typeof fromRaw === 'string' ? relativeRange(fromRaw) : null;
  const toRange = typeof toRaw === 'string' ? relativeRange(toRaw) : null;
  if (fromRange && toRaw == null) {
    action.checkIn = fromRange.from;
    action.checkOut = fromRange.to;
  } else if (toRange && fromRaw == null) {
    action.checkIn = toRange.from;
    action.checkOut = toRange.to;
  } else {
    if (typeof fromRaw === 'string') {
      action.checkIn = fromRange?.from || toOrderDate(fromRaw) || undefined;
    }
    if (typeof toRaw === 'string') {
      action.checkOut = toRange?.to || toOrderDate(toRaw) || undefined;
    }
  }

  const dateMode = read(source, ['searchByCreateDate', 'byCreateDate', 'creationDate', 'dateMode']);
  if (typeof dateMode === 'string') {
    const key = norm(dateMode);
    if (['creation', 'created', 'create', 'creation date', 'booked', 'booking'].includes(key)) {
      action.searchByCreateDate = true;
    } else if (['checkin', 'check in', 'service', 'travel'].includes(key)) {
      action.searchByCreateDate = false;
    } else {
      const parsed = asBool(dateMode);
      if (parsed != null) action.searchByCreateDate = parsed;
    }
  } else {
    const parsed = asBool(dateMode);
    if (parsed != null) action.searchByCreateDate = parsed;
  }

  const status = read(source, ['orderStatus', 'status']);
  if (status === null || status === '') action.status = null;
  else if (Array.isArray(status) || typeof status === 'string') {
    action.status = Array.isArray(status) ? status.map((item) => String(item)).join(';') : status;
  }

  const supplier = asText(read(source, ['orderSupplier', 'supplier', 'supplierCode']));
  if (supplier !== undefined) action.supplier = supplier;

  const product = asText(read(source, ['orderProductType', 'product', 'productType', 'type']));
  if (product !== undefined) action.product = product;

  const department = asText(read(source, ['department', 'departmentId', 'departmentName']));
  if (department !== undefined) action.department = department;

  const branch = asText(read(source, ['branch', 'branchId', 'branchName']));
  if (branch !== undefined) action.branch = branch;

  const user = asText(read(source, ['user', 'userCode', 'userName', 'agent']));
  if (user !== undefined) action.user = user;

  const orderId = read(source, ['orderId', 'orderID']);
  const segment = read(source, ['segmentId', 'segmentIds', 'orderSegmentId', 'orderSegmentIds']);
  if (orderId != null && asText(orderId)) {
    action.segmentText = asText(orderId) ?? null;
    action.searchByOrderId = true;
  } else if (segment !== undefined) {
    action.segmentText = asText(segment) ?? null;
    action.searchByOrderId = false;
  }
  const byOrder = asBool(read(source, ['searchByOrderId', 'byOrderId']));
  if (byOrder != null) action.searchByOrderId = byOrder;

  const pnr = asText(read(source, ['orderSupplierPNR', 'supplierPnr', 'supplierPNR', 'pnr']));
  if (pnr !== undefined) action.supplierPnr = pnr;

  const excludeDates = asBool(read(source, ['excludeDates', 'ignoreDates']));
  if (excludeDates != null) action.excludeDates = excludeDates;

  const backOffice = asText(read(source, ['orderBackOfficeId', 'backofficeId', 'backOfficeId', 'docket']));
  if (backOffice !== undefined) action.backOfficeId = backOffice;

  const passenger = asText(read(source, ['orderPaxName', 'passenger', 'passengerName', 'pax', 'paxName']));
  if (passenger !== undefined) action.passenger = passenger;

  const item = asText(read(source, ['orderItem', 'item', 'itemName']));
  if (item !== undefined) action.item = item;

  const filter = asText(read(source, ['filter', 'orderFilter', 'mainFilter', 'preset']));
  if (filter !== undefined) action.filter = filter;

  return action;
};

const optionName = (option: Select) => option.label.split('|')[0]?.trim() || option.label;

const matchOption = (options: Select[] | undefined, query: string): string | null => {
  if (!options?.length || !query.trim()) return null;
  const key = norm(query);
  const exact = options.find(
    (option) =>
      norm(String(option.value)) === key ||
      norm(option.label) === key ||
      norm(optionName(option)) === key ||
      norm(option.label.split('|')[1] || '') === key,
  );
  if (exact) return String(exact.value);
  if (key.length < 3) return null;
  const partial = options.find((option) => norm(optionName(option)).startsWith(key));
  return partial ? String(partial.value) : null;
};

const resolveStatus = (raw: string | null, options?: Select[]) => {
  if (raw == null || !raw.trim() || isAll(raw)) return 'ALL';
  const tokens = raw
    .split(/[;,|/]+/)
    .map((token) => STATUS_ALIASES[norm(token)] || token.trim().toUpperCase())
    .filter(Boolean);
  if (!tokens.length || tokens.includes('ALL')) return 'ALL';
  const wanted = Array.from(new Set(tokens)).sort().join(';');
  const option = options?.find((item) => {
    const value = String(item.value).toUpperCase().split(';').filter(Boolean).sort().join(';');
    return value === wanted || norm(item.label) === norm(raw);
  });
  if (option) return String(option.value);
  return tokens.join(';');
};

const resolveProduct = (raw: string | null, options?: Select[]) => {
  if (raw == null || !raw.trim() || isAll(raw)) return '';
  const code = PRODUCT_ALIASES[norm(raw)];
  const matched = (code && matchOption(options, code)) || matchOption(options, raw);
  if (matched != null) return matched;
  return code || null;
};

const resolveNamedId = (raw: string | null, options?: Select[]): number | undefined => {
  if (raw == null || !raw.trim() || isAll(raw)) return -1;
  const matched = matchOption(options, raw);
  if (matched == null) return undefined;
  const id = Number(matched);
  return Number.isFinite(id) ? id : undefined;
};

const filterKey = (value: string) =>
  norm(value).replace(/[():]/g, ' ').replace(/\s+/g, ' ').trim();

const resolveFilter = (raw: string | null): string | undefined => {
  if (raw == null || !raw.trim() || isAll(raw)) return MainMoFilter.NoFilter;
  const alias = FILTER_ALIASES[filterKey(raw)] ?? FILTER_ALIASES[norm(raw)];
  if (alias != null) return alias;
  const option = moSearchFilter.find(
    (item) => item.value === raw || filterKey(item.label) === filterKey(raw),
  );
  return option ? String(option.value) : undefined;
};

const COLUMN_FILTERS: Record<string, string> = {
  seg: 'segmentId',
  segment: 'segmentId',
  'segment id': 'segmentId',
  docket: 'docket',
  'back office': 'docket',
  backoffice: 'docket',
  order: 'orderId',
  orderid: 'orderId',
  'order id': 'orderId',
  supp: 'suppPnr',
  pnr: 'suppPnr',
  supplier: 'suppPnr',
  'supplier pnr': 'suppPnr',
  'supp pnr': 'suppPnr',
  'supplier name': 'supplier',
  checkin: 'checkIn',
  'check in': 'checkIn',
  product: 'product',
  department: 'department',
  dept: 'department',
  user: 'userCode',
  name: 'name',
  passenger: 'name',
  pax: 'name',
  dest: 'dest',
  destination: 'dest',
  item: 'items',
  items: 'items',
  clx: 'clx',
  cancellation: 'clx',
  status: 'status',
  q: 'quantity',
  qty: 'quantity',
  quantity: 'quantity',
  total: 'total',
  price: 'total',
  ticket: 'ticketNumber',
  'ticket n': 'ticketNumber',
  'ticket number': 'ticketNumber',
};

const resolveColumn = (value: string) => COLUMN_FILTERS[filterKey(value)];

const withVisibility = (options: SearchRequest, departmentId: number, branchId: number): SearchRequest => ({
  ...options,
  branchesOptions: (options.branchesOptions || []).map((branch) => ({
    ...branch,
    data: {
      ...branch.data,
      visible:
        branch.value === '-1' ||
        branch.data?.departmentId === departmentId ||
        branch.data?.departmentId === -1 ||
        departmentId === -1,
    },
  })),
  usersOptions: (options.usersOptions || []).map((user) => ({
    ...user,
    data: {
      ...user.data,
      visible:
        user.value === '-1' ||
        ((departmentId === -1 ||
          user.data?.departmentId === departmentId ||
          user.data?.departmentId === -1) &&
          (branchId === -1 || user.data?.branchId === branchId || user.data?.branchId === -1)),
    },
  })),
});

const segmentIds = (value: string) =>
  value
    .replace(/\D+/g, ' ')
    .trim()
    .split(' ')
    .filter((part) => part.length > 3)
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part));

export const applyOrdersSearch = (
  model: SearchRequest,
  options: SearchRequest,
  action: OrdersSearchAction,
): AppliedOrdersSearch => {
  const next: SearchRequest = { ...model };
  let departmentId = next.departmentId;
  let branchId = next.branchId;
  let userCode = next.userCode;
  let mainFilter: string | undefined;
  let orderFilter: string | undefined;
  let segmentText: string | null | undefined;

  if (action.filter !== undefined) {
    const filter = resolveFilter(action.filter);
    if (filter != null) {
      mainFilter = filter;
      if (filter === MainMoFilter.LastMonthSegments || filter === MainMoFilter.CurrentMonthSegments) {
        const month =
          filter === MainMoFilter.LastMonthSegments ? subMonths(new Date(), 1) : new Date();
        next.orderCheckIn = startOfMonth(month).toISOString();
        next.orderCheckOut = endOfMonth(month).toISOString();
        next.orderStatus = 'RQ;OK;HK;HX;VOUCHER';
        next.orderSupplier = '';
        next.orderProductType = '';
        if (!model.selectedDepartmentId) departmentId = -1;
        if (!model.selectedBranchId) branchId = -1;
        if (!model.selectedUserCode) userCode = -1;
      }
      if (filter === MainMoFilter.KeepDelRows) {
        orderFilter = 'KeepDelRows';
      }
    }
  }

  if (action.checkIn) next.orderCheckIn = action.checkIn;
  if (action.checkOut) next.orderCheckOut = action.checkOut;
  if (action.searchByCreateDate != null) next.searchByCreateDate = action.searchByCreateDate;

  if (action.status !== undefined) {
    next.orderStatus = resolveStatus(action.status, options.statusesOptions);
  }
  if (action.supplier !== undefined) {
    if (action.supplier == null || !action.supplier.trim() || isAll(action.supplier)) {
      next.orderSupplier = '';
    } else {
      const matched = matchOption(options.supplierOptions, action.supplier);
      if (matched != null) next.orderSupplier = matched;
    }
  }
  if (action.product !== undefined) {
    const product = resolveProduct(action.product, options.typesOptions);
    if (product != null) next.orderProductType = product;
  }

  if (action.department !== undefined && !model.selectedDepartmentId) {
    const resolved = resolveNamedId(action.department, options.departmentsOptions);
    if (resolved != null) departmentId = resolved;
  }
  if (action.branch !== undefined && !model.selectedBranchId) {
    const resolved = resolveNamedId(action.branch, options.branchesOptions);
    if (resolved != null) branchId = resolved;
  } else if (departmentId !== model.departmentId && !model.selectedBranchId && action.branch === undefined) {
    branchId = -1;
  }
  if (action.user !== undefined && !model.selectedUserCode) {
    const resolved = resolveNamedId(action.user, options.usersOptions);
    if (resolved != null) userCode = resolved;
  } else if (
    (departmentId !== model.departmentId || branchId !== model.branchId) &&
    !model.selectedUserCode &&
    action.user === undefined
  ) {
    userCode = -1;
  }

  next.departmentId = departmentId;
  next.branchId = branchId;
  next.userCode = userCode;

  if (action.segmentText !== undefined) {
    segmentText = action.segmentText?.trim() || '';
    if (!segmentText) {
      next.orderSegmentId = undefined;
      next.orderSegmentIds = undefined;
    } else {
      const ids = segmentIds(segmentText);
      if (ids.length <= 1) {
        next.orderSegmentId = ids[0];
        next.orderSegmentIds = undefined;
      } else {
        next.orderSegmentId = undefined;
        next.orderSegmentIds = ids;
      }
    }
  }
  if (action.searchByOrderId != null) next.searchByOrderId = action.searchByOrderId;

  if (action.supplierPnr !== undefined) next.orderSupplierPNR = action.supplierPnr?.trim() || '';
  if (action.backOfficeId !== undefined) next.orderBackOfficeId = action.backOfficeId?.trim() || '';
  if (action.passenger !== undefined) next.orderPaxName = action.passenger?.trim() || '';
  if (action.item !== undefined) next.orderItem = action.item?.trim() || '';

  const hasDateException = !!(
    next.orderSupplierPNR ||
    next.searchByOrderId ||
    (action.segmentText !== undefined ? segmentText : next.orderSegmentId || next.orderSegmentIds?.length)
  );
  if (action.excludeDates != null) {
    next.excludeDates = action.excludeDates && hasDateException;
  } else if (!hasDateException) {
    next.excludeDates = false;
  }

  const visibilityChanged = departmentId !== model.departmentId || branchId !== model.branchId;
  return {
    model: next,
    options: visibilityChanged ? withVisibility(options, departmentId, branchId) : undefined,
    segmentText,
    mainFilter,
    orderFilter,
  };
};

let pendingSearch: OrdersSearchAction | null = null;
const searchListeners = new Set<() => void>();
let searchVersion = 0;

const notifySearch = () => {
  searchVersion += 1;
  searchListeners.forEach((listener) => listener());
};

export const queueOrdersSearch = (action: OrdersSearchAction) => {
  pendingSearch = action;
  notifySearch();
};

export const peekOrdersSearch = () => pendingSearch;

export const hasPendingOrdersSearch = () => pendingSearch != null;

export const takeOrdersSearch = () => {
  const action = pendingSearch;
  pendingSearch = null;
  return action;
};

export const subscribeOrdersSearch = (listener: () => void) => {
  searchListeners.add(listener);
  return () => {
    searchListeners.delete(listener);
  };
};

export const getOrdersSearchVersion = () => searchVersion;

export const ORDER_ACTION_KINDS = [
  'print',
  'pnr-to-docket',
  'special-request',
  'voucher',
  'save-docket',
  'status',
  'cancel',
  'support-ticket',
  'save-report',
  'change-user',
  'support-update',
  'se-general',
  'se-gross',
  'se-rooms',
  'se-details',
  'lock-pnr',
  'open-logs',
  'copy-json',
  'tab',
  'copy',
  'add-remark',
  'add-internal-remark',
  'close-remark',
  'add-payment',
  'upload-document',
  'download-document',
  'delete-document',
  'ticket-reply',
  'filter',
  'table-settings',
  'select',
] as const;

export type OrdersActionKind = (typeof ORDER_ACTION_KINDS)[number];

export type OrdersPrintReport = 'information' | 'public' | 'agent' | 'operator' | 'email';

export interface OrdersAction {
  action: OrdersActionKind;
  report?: OrdersPrintReport;
  message?: string;
  subject?: string;
  voucherNumber?: string;
  thin?: boolean;
  docket?: string;
  docketType?: 1 | 2;
  confirm?: boolean;
  userCode?: number;
  commentPublic?: boolean;
  ticketId?: number;
  segmentId?: number;
  orderId?: number;
  index?: number;
  targetId?: number;
  tab?: string;
  tabScope?: 'page' | 'order';
  copyField?: 'item' | 'segment' | 'order';
  remarkCode?: string;
  remarkId?: number;
  documentId?: number;
  documentName?: string;
  tableFilter?: string;
  preset?: string;
  columnFilters?: { id: string; value: string }[];
  clearFilters?: boolean;
}

const ACTION_ALIASES: Record<string, OrdersActionKind> = {
  print: 'print',
  'pnr to docket': 'pnr-to-docket',
  'pnr-to-docket': 'pnr-to-docket',
  pnrtodocket: 'pnr-to-docket',
  pnr: 'pnr-to-docket',
  'special request': 'special-request',
  'special-request': 'special-request',
  'change request': 'special-request',
  'change-request': 'special-request',
  voucher: 'voucher',
  'save docket': 'save-docket',
  'save-docket': 'save-docket',
  status: 'status',
  'check status': 'status',
  'check-status': 'status',
  'show status': 'status',
  'open status': 'status',
  cancel: 'cancel',
  'cancel order': 'cancel',
  'cancel-order': 'cancel',
  'support ticket': 'support-ticket',
  'support-ticket': 'support-ticket',
  ticket: 'support-ticket',
  'save report': 'save-report',
  'save-report': 'save-report',
  'change user': 'change-user',
  'change-user': 'change-user',
  'support update': 'support-update',
  'support-update': 'support-update',
  'se general': 'se-general',
  'se-general': 'se-general',
  's.e. general': 'se-general',
  'se gross': 'se-gross',
  'se-gross': 'se-gross',
  's.e. gross': 'se-gross',
  'se rooms': 'se-rooms',
  'se-rooms': 'se-rooms',
  'rooms and paxes': 'se-rooms',
  'rooms & paxes': 'se-rooms',
  'se details': 'se-details',
  'se-details': 'se-details',
  's.e. details': 'se-details',
  'lock pnr': 'lock-pnr',
  'lock-pnr': 'lock-pnr',
  'unlock pnr': 'lock-pnr',
  'unlock-pnr': 'lock-pnr',
  logs: 'open-logs',
  'open logs': 'open-logs',
  'open-logs': 'open-logs',
  'copy json': 'copy-json',
  'copy-json': 'copy-json',
  'copy order json': 'copy-json',
  copy: 'copy',
  'copy item': 'copy',
  'copy name': 'copy',
  'copy segment': 'copy',
  'copy segment id': 'copy',
  'copy order id': 'copy',
  tab: 'tab',
  'open tab': 'tab',
  'go to': 'tab',
  goto: 'tab',
  'add remark': 'add-remark',
  'add-remark': 'add-remark',
  remark: 'add-remark',
  'add internal remark': 'add-internal-remark',
  'add-internal-remark': 'add-internal-remark',
  'private remark': 'add-internal-remark',
  'add private remark': 'add-internal-remark',
  'close remark': 'close-remark',
  'close-remark': 'close-remark',
  'close request': 'close-remark',
  'add payment': 'add-payment',
  'add-payment': 'add-payment',
  upload: 'upload-document',
  'upload document': 'upload-document',
  'upload-document': 'upload-document',
  'add document': 'upload-document',
  download: 'download-document',
  'download document': 'download-document',
  'download-document': 'download-document',
  'open document': 'download-document',
  'delete document': 'delete-document',
  'delete-document': 'delete-document',
  'remove document': 'delete-document',
  'ticket reply': 'ticket-reply',
  'ticket-reply': 'ticket-reply',
  reply: 'ticket-reply',
  filter: 'filter',
  'table filter': 'filter',
  'filter table': 'filter',
  'column filter': 'filter',
  'clear filter': 'filter',
  'clear filters': 'filter',
  'reset filter': 'filter',
  'reset filters': 'filter',
  'table settings': 'table-settings',
  'table-settings': 'table-settings',
  columns: 'table-settings',
  select: 'select',
  choose: 'select',
  focus: 'select',
  activate: 'select',
  'select order': 'select',
  'open order': 'select',
  'active order': 'select',
  'set active': 'select',
  'set-active': 'select',
  'set active order': 'select',
  'switch order': 'select',
  'change order': 'select',
  'show payments': 'tab',
  'show payment': 'tab',
  'open payments': 'tab',
  'open payment': 'tab',
  payments: 'tab',
  payment: 'tab',
};

const PAGE_TABS: Record<string, string> = {
  orders: 'orders',
  list: 'orders',
  'my orders': 'orders',
  home: 'orders',
  segment: 'segment',
  'segment view': 'segment',
  'open segment': 'segment',
  logs: 'logs',
  log: 'logs',
};

const ORDER_TABS: Record<string, string> = {
  summary: 'summary',
  overview: 'summary',
  main: 'summary',
  general: 'summary',
  product: 'summary',
  details: 'details',
  'flight details': 'details',
  'segment details': 'details',
  rooms: 'rooms',
  room: 'rooms',
  cxl: 'cxl',
  cancellation: 'cxl',
  'cancellation date': 'cxl',
  supplier: 'supplier',
  amount: 'amount',
  price: 'amount',
  remarks: 'remarks',
  remark: 'remarks',
  'internal remarks': 'internal-remarks',
  'internal remark': 'internal-remarks',
  internal: 'internal-remarks',
  paxes: 'paxes',
  pax: 'paxes',
  passengers: 'paxes',
  passenger: 'paxes',
  payments: 'payments',
  payment: 'payments',
  info: 'info',
  information: 'info',
  documents: 'documents',
  document: 'documents',
  docs: 'documents',
  files: 'documents',
  ticket: 'ticket',
};

const REMARK_CODES: Record<string, string> = {
  appman: 'APPMAN',
  manual: 'APPMAN',
  response: 'APPMAN',
  'response manual': 'APPMAN',
  appyes: 'APPYES',
  phone: 'APPYES',
  approval: 'APPYES',
  'approval phone': 'APPYES',
  appart: 'APPART',
  alert: 'APPART',
  'segment alert': 'APPART',
  appopn: 'APPOPN',
  task: 'APPOPN',
  'open task': 'APPOPN',
  'segment open task': 'APPOPN',
  intop: 'INTOP',
  private: 'INTOP',
  internal: 'INTOP',
};

const cleanTabName = (value: string) => norm(value).replace(/\(\d+\)/g, '').replace(/\s+/g, ' ').trim();

export const resolveOrdersTab = (
  value: string,
): { scope: 'page' | 'order'; tab: string } | null => {
  const key = cleanTabName(value);
  if (!key) return null;
  if (PAGE_TABS[key]) return { scope: 'page', tab: PAGE_TABS[key] };
  if (ORDER_TABS[key]) return { scope: 'order', tab: ORDER_TABS[key] };
  if (key.startsWith('internal')) return { scope: 'order', tab: 'internal-remarks' };
  const stripped = key.replace(/^(?:show|open|go to|goto|switch to|view)\s+/, '');
  if (stripped !== key) {
    if (PAGE_TABS[stripped]) return { scope: 'page', tab: PAGE_TABS[stripped] };
    if (ORDER_TABS[stripped]) return { scope: 'order', tab: ORDER_TABS[stripped] };
  }
  const orderKey = Object.keys(ORDER_TABS).find((item) => key.startsWith(item) || stripped.startsWith(item));
  if (orderKey) return { scope: 'order', tab: ORDER_TABS[orderKey] };
  return null;
};

export const ORDER_PANEL_TAB_ID: Record<string, number> = {
  summary: 4,
  rooms: 5,
  details: 5,
  cxl: 8,
  supplier: 9,
  amount: 6,
  remarks: 0,
  'internal-remarks': 11,
  paxes: 7,
  payments: 3,
  info: 1,
  documents: 2,
  ticket: 10,
};

export const impliedOrderTab = (action: OrdersActionKind): string | null => {
  switch (action) {
    case 'add-remark':
    case 'close-remark':
      return 'remarks';
    case 'add-internal-remark':
      return 'internal-remarks';
    case 'add-payment':
      return 'payments';
    case 'upload-document':
    case 'download-document':
    case 'delete-document':
      return 'documents';
    case 'ticket-reply':
      return 'ticket';
    case 'copy':
      return 'summary';
    case 'filter':
    case 'table-settings':
    case 'select':
      return null;
    default:
      return null;
  }
};

const PRINT_REPORTS: Record<string, OrdersPrintReport> = {
  information: 'information',
  info: 'information',
  'order information': 'information',
  public: 'public',
  'public price': 'public',
  'order public price': 'public',
  agent: 'agent',
  operator: 'operator',
  email: 'email',
  'send email': 'email',
};

const parseIndexToken = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1) return Math.floor(value);
  if (typeof value !== 'string') return undefined;
  const text = norm(value);
  const match = text.match(/^(?:#\s*)?(?:n|row|no|number)?\s*#?\s*(\d+)$/);
  if (!match) return undefined;
  const index = Number(match[1]);
  return index >= 1 ? index : undefined;
};

const parseSelectName = (
  nameKey: string,
): { index?: number; segmentId?: number; orderId?: number; targetId?: number } | null => {
  const hash = nameKey.match(/^#\s*(\d+)$/);
  if (hash) return { index: Number(hash[1]) };

  const segment = nameKey.match(/^(?:segment|seg)\s+#?\s*(\d+)$/);
  if (segment) {
    const id = Number(segment[1]);
    return nameKey.includes('#') ? { index: id } : { segmentId: id };
  }

  const order = nameKey.match(/^order\s+#?\s*(\d+)$/);
  if (order) {
    const id = Number(order[1]);
    return nameKey.includes('#') ? { index: id } : { orderId: id };
  }

  const select = nameKey.match(
    /^(?:select|choose|focus|activate|open|active|switch|change|set\s+active)(?:\s+order|\s+segment|\s+seg)?\s+#?\s*(\d+)$/,
  );
  if (!select) return null;
  const id = Number(select[1]);
  if (nameKey.includes('#')) return { index: id };
  if (/\b(?:segment|seg)\b/.test(nameKey)) return { segmentId: id };
  if (/\border\b/.test(nameKey)) return { orderId: id };
  return { targetId: id };
};

const asActionNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return undefined;
};

const parseFilterPhrase = (nameKey: string) => {
  if (/^(?:clear|reset) filters?$/.test(nameKey)) return { clear: true };
  const lead = nameKey.match(/^(?:filter|column filter)(?:\s+by)?\s+(.+)$/);
  if (!lead) return null;
  const rest = lead[1];
  const preset = resolveFilter(rest);
  if (preset !== undefined) return { preset };
  const columnName = Object.keys(COLUMN_FILTERS)
    .sort((left, right) => right.length - left.length)
    .find((key) => rest === key || rest.startsWith(`${key} `));
  if (columnName) {
    const value = rest.slice(columnName.length).trim();
    if (value) return { columns: [{ id: COLUMN_FILTERS[columnName], value }] };
  }
  return { text: rest };
};

const columnFiltersFrom = (source: Record<string, unknown>) => {
  const filters: { id: string; value: string }[] = [];
  const raw = readAction(source, ['columns', 'columnFilters']);
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      const id = resolveColumn(key);
      if (!id || (typeof value !== 'string' && typeof value !== 'number')) return;
      filters.push({ id, value: String(value) });
    });
  }
  const columnName = asText(readAction(source, ['column', 'field']));
  if (columnName) {
    const id = resolveColumn(columnName);
    const value = asText(readAction(source, ['columnValue', 'value'])) ?? '';
    if (id) filters.push({ id, value });
  }
  return filters;
};

const readAction = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) return source[key];
  }
  return undefined;
};

export const parseOrdersActionPayload = (
  raw: Record<string, unknown> | null | undefined,
): OrdersAction | null => {
  if (!raw || typeof raw !== 'object') return null;
  const source =
    raw.query && typeof raw.query === 'object' && !Array.isArray(raw.query)
      ? { ...raw, ...(raw.query as Record<string, unknown>) }
      : raw;
  const name = asText(readAction(source, ['action', 'name', 'tool', 'button']));
  const nameKey = name ? norm(name) : '';
  let action = nameKey ? ACTION_ALIASES[nameKey] : undefined;
  const fromName = !action && nameKey ? parseSelectName(nameKey) : null;
  const fromFilter = !action && !fromName && nameKey ? parseFilterPhrase(nameKey) : null;
  const presetFromName =
    !action && !fromName && !fromFilter && nameKey ? resolveFilter(nameKey) : undefined;
  const explicitTab = asText(readAction(source, ['tab', 'page', 'view', 'panel']));
  const resolvedFromName =
    !fromName && !fromFilter && presetFromName === undefined && nameKey
      ? resolveOrdersTab(nameKey)
      : null;
  if (!action && fromName) action = 'select';
  if (!action && (fromFilter || presetFromName !== undefined)) action = 'filter';
  if (!action && resolvedFromName) action = 'tab';
  if (!action && explicitTab && resolveOrdersTab(explicitTab)) action = 'tab';
  const indexRaw = parseIndexToken(
    readAction(source, ['index', 'position', 'row', 'n', '#']),
  );
  const segmentFromPayload = asActionNumber(
    readAction(source, ['segmentId', 'orderSegmentId', 'segId', 'seg']),
  );
  const orderFromPayload = asActionNumber(readAction(source, ['orderId']));
  const targetFromPayload = asActionNumber(readAction(source, ['targetId', 'id']));
  if (
    !action &&
    (indexRaw || segmentFromPayload || orderFromPayload || targetFromPayload)
  ) {
    action = 'select';
  }
  if (!action) return null;

  const reportName = asText(readAction(source, ['report', 'reportType', 'print']));
  const report = reportName ? PRINT_REPORTS[norm(reportName)] : undefined;
  const docketTypeRaw = asActionNumber(readAction(source, ['docketType', 'type']));
  const resolvedTab =
    action === 'tab'
      ? resolveOrdersTab(explicitTab || nameKey) || resolvedFromName || (explicitTab ? resolveOrdersTab(explicitTab) : null)
      : null;
  if (action === 'tab' && !resolvedTab) return null;

  const remarkCodeRaw = asText(readAction(source, ['remarkCode', 'code']));
  const remarkCode = remarkCodeRaw
    ? REMARK_CODES[norm(remarkCodeRaw)] || remarkCodeRaw.trim().toUpperCase()
    : undefined;
  const copyField: OrdersAction['copyField'] =
    nameKey.includes('segment') ? 'segment' : nameKey.includes('order id') ? 'order' : undefined;
  const copyFieldRaw = asText(readAction(source, ['field', 'copyField']));
  const resolvedCopyField: OrdersAction['copyField'] =
    copyFieldRaw && norm(copyFieldRaw).includes('segment')
      ? 'segment'
      : copyFieldRaw && norm(copyFieldRaw).includes('order')
        ? 'order'
        : copyField || 'item';

  let resolvedAction = action;
  if (resolvedAction === 'add-remark' && remarkCode === 'INTOP') resolvedAction = 'add-internal-remark';

  return {
    action: resolvedAction,
    report,
    message: asText(readAction(source, ['message', 'text', 'request', 'notes'])) || undefined,
    subject: asText(readAction(source, ['subject'])) || undefined,
    voucherNumber: asText(readAction(source, ['voucherNumber', 'voucher', 'boVoucher'])) || undefined,
    thin: asBool(readAction(source, ['thin', 'thinReport'])),
    docket: asText(readAction(source, ['docket', 'backOfficeId', 'docketNumber'])) || undefined,
    docketType: docketTypeRaw === 2 ? 2 : docketTypeRaw === 1 ? 1 : undefined,
    confirm: asBool(readAction(source, ['confirm', 'yes', 'submit'])),
    userCode: asActionNumber(readAction(source, ['userCode', 'targetUserCode'])),
    commentPublic: asBool(readAction(source, ['commentPublic', 'public'])),
    ticketId: asActionNumber(readAction(source, ['ticketId'])),
    segmentId: segmentFromPayload ?? fromName?.segmentId,
    orderId: orderFromPayload ?? fromName?.orderId,
    index: indexRaw ?? fromName?.index,
    targetId:
      segmentFromPayload || orderFromPayload ? undefined : targetFromPayload ?? fromName?.targetId,
    tab: resolvedTab?.tab,
    tabScope: resolvedTab?.scope,
    copyField: resolvedAction === 'copy' ? resolvedCopyField : undefined,
    remarkCode,
    remarkId: asActionNumber(readAction(source, ['remarkId', 'orderSegRemarkId'])),
    documentId: asActionNumber(readAction(source, ['documentId', 'orderDocumentId'])),
    documentName: asText(readAction(source, ['documentName', 'fileName', 'file'])) || undefined,
    ...filterFields(source, resolvedAction, nameKey, fromFilter, presetFromName),
  };
};

const filterFields = (
  source: Record<string, unknown>,
  action: OrdersActionKind,
  nameKey: string,
  fromFilter: { clear?: boolean; preset?: string; text?: string; columns?: { id: string; value: string }[] } | null,
  presetFromName?: string,
): Pick<OrdersAction, 'tableFilter' | 'preset' | 'columnFilters' | 'clearFilters'> => {
  if (action !== 'filter') return {};
  const presetField = asText(readAction(source, ['preset', 'mainFilter']));
  const filterField = asText(readAction(source, ['filter']));
  const textField = asText(readAction(source, ['tableFilter', 'filterText', 'queryText', 'text', 'query']));
  let preset = presetField != null ? resolveFilter(presetField) : undefined;
  let tableFilter = textField ?? undefined;
  if (filterField != null) {
    const asPreset = resolveFilter(filterField);
    if (asPreset !== undefined && preset === undefined) preset = asPreset;
    else if (asPreset === undefined && tableFilter === undefined) tableFilter = filterField;
  }
  if (preset === undefined) preset = fromFilter?.preset ?? presetFromName;
  if (tableFilter === undefined && fromFilter?.text) tableFilter = fromFilter.text;
  const message = asText(readAction(source, ['message', 'text', 'request', 'notes']));
  if (tableFilter === undefined && message && preset === undefined) {
    const asPreset = resolveFilter(message);
    if (asPreset !== undefined) preset = asPreset;
    else tableFilter = message;
  }
  const columnFilters = [...(fromFilter?.columns || []), ...columnFiltersFrom(source)];
  const clearFilters =
    !!fromFilter?.clear ||
    /^(?:clear|reset) filters?$/.test(nameKey) ||
    asBool(readAction(source, ['clearFilters', 'reset'])) === true;
  if (
    preset === undefined &&
    tableFilter === undefined &&
    !columnFilters.length &&
    !clearFilters
  ) {
    tableFilter = '';
  }
  return {
    preset,
    tableFilter,
    columnFilters: columnFilters.length ? columnFilters : undefined,
    clearFilters: clearFilters || undefined,
  };
};

export const actionTargetsOrder = (
  action: OrdersAction,
  order: { segmentId?: number; orderId?: number; altSegmentId?: number; altOrderId?: number },
  isActive: boolean,
) => {
  if ((action.index || action.targetId) && !action.segmentId && !action.orderId) return false;
  const ids = [action.segmentId, action.orderId, action.targetId]
    .filter((id): id is number => id != null)
    .map(Number);
  if (ids.length) {
    const orderIds = [order.segmentId, order.altSegmentId, order.orderId, order.altOrderId]
      .filter((id) => id != null)
      .map(Number);
    return ids.some((id) => orderIds.includes(id));
  }
  return isActive;
};

const pendingOrderActions: OrdersAction[] = [];
const orderActionListeners = new Set<() => void>();
let orderActionVersion = 0;

const notifyOrderActions = () => {
  orderActionVersion += 1;
  orderActionListeners.forEach((listener) => listener());
};

export const queueOrdersAction = (action: OrdersAction) => {
  pendingOrderActions.push(action);
  notifyOrderActions();
};

export const peekOrdersActions = () => pendingOrderActions.slice();

export const patchOrdersAction = (current: OrdersAction, next: OrdersAction) => {
  const index = pendingOrderActions.indexOf(current);
  if (index === -1) return;
  pendingOrderActions[index] = next;
  notifyOrderActions();
};

export const consumeOrdersActions = (match: (action: OrdersAction) => boolean) => {
  const taken: OrdersAction[] = [];
  const kept: OrdersAction[] = [];
  pendingOrderActions.forEach((action) => (match(action) ? taken : kept).push(action));
  pendingOrderActions.length = 0;
  pendingOrderActions.push(...kept);
  return taken;
};

export const subscribeOrdersActions = (listener: () => void) => {
  orderActionListeners.add(listener);
  return () => {
    orderActionListeners.delete(listener);
  };
};

export const getOrdersActionsVersion = () => orderActionVersion;

export const useOrdersAction = (
  match: (action: OrdersAction) => boolean,
  onAction: (action: OrdersAction) => void,
  active: unknown = true,
) => {
  const matchRef = useRef(match);
  const onActionRef = useRef(onAction);
  matchRef.current = match;
  onActionRef.current = onAction;

  useEffect(() => {
    if (active === false || active === 0 || active === '' || active == null) {
      return;
    }
    const run = () => {
      const taken = consumeOrdersActions((action) => matchRef.current(action));
      if (!taken.length) return;
      const row = document.querySelector('.mo-actions-row');
      if (row instanceof HTMLElement) row.scrollIntoView({ block: 'nearest' });
      highlightAssistantTargets(['orders-actions']);
      taken.forEach((action) => onActionRef.current(action));
    };
    run();
    return subscribeOrdersActions(run);
  }, [active]);
};

export interface OrdersListFilter {
  text?: string;
  columns: { id: string; value: string }[];
  clearText: boolean;
  clearColumns: boolean;
}

let listFilter: OrdersListFilter = { columns: [], clearText: false, clearColumns: false };
let listFilterVersion = 0;
const listFilterListeners = new Set<() => void>();

export const publishOrdersListFilter = (update: OrdersListFilter) => {
  listFilter = update;
  listFilterVersion += 1;
  listFilterListeners.forEach((listener) => listener());
};

export const getOrdersListFilter = () => listFilter;

export const getOrdersListFilterVersion = () => listFilterVersion;

export const subscribeOrdersListFilter = (listener: () => void) => {
  listFilterListeners.add(listener);
  return () => {
    listFilterListeners.delete(listener);
  };
};
